<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import { messageOf } from '@/utils/messages'
import { won } from '../../common/public-courses.mjs'
import AppIcon from '@/components/portal/AppIcon.vue'
// 교육신청하기(레거시 lectureSubmit 모달). 단체 신청은 기업 교육담당자를 통하고, 로그인한 학습자는 소속 기업이 신청한 학습기간에 바로 수강 신청(승인 대기)한다.
// 결제·주민등록번호는 받지 않는다(레거시 신청창의 해당 항목은 제공하지 않음).
const props = defineProps({ course: { type: Object, default: null } })
const emit = defineEmits(['close'])
const auth = useAuthStore(), route = useRoute()
const choices = ref([]), state = ref('idle'), error = ref(''), selected = ref(''), reason = ref(''), busy = ref(false), receipt = ref(null)
const fee = computed(() => { const row = props.course; if (!row) return null; return row.refund ? row.standardFee : row.legacy?.fee || null })
const loginTo = computed(() => ({ path: '/login', query: { returnTo: route.fullPath } }))
const choice = computed(() => choices.value.find(item => `${item.openingId}` === selected.value) || null)
let epoch = 0, draft = null
async function load() {
  const current = ++epoch; choices.value = []; selected.value = ''; error.value = ''; receipt.value = null; reason.value = ''; draft = null
  if (!props.course || !auth.isLoggedIn) { state.value = 'guest'; return }
  state.value = 'loading'
  try {
    const data = await auth.client.request(`/api/user/enrollment-applications/openings?${new URLSearchParams({ page: 1, size: 100 })}`)
    if (current !== epoch) return
    choices.value = data.items.filter(item => item.productId === props.course.id)
    selected.value = choices.value.length === 1 ? `${choices.value[0].openingId}` : ''
    state.value = 'ready'
  } catch (cause) { if (current === epoch) { state.value = 'error'; error.value = [401, 403].includes(cause.status) ? '이 계정으로는 수강 신청을 할 수 없습니다. 소속 기업의 교육담당자에게 문의해 주세요.' : messageOf(cause) } }
}
watch(() => [props.course?.id, auth.isLoggedIn], () => { if (props.course) load() }, { immediate: true })
// 응답을 잃은 재시도도 같은 내용이면 같은 요청 번호를 쓴다.
async function submit() {
  if (!choice.value || busy.value) return
  busy.value = true; error.value = ''
  const payload = { openingId: choice.value.openingId, openingVersion: choice.value.version, siteCode: choice.value.siteCode, reason: reason.value.trim() }
  const fingerprint = JSON.stringify(payload)
  if (draft?.fingerprint !== fingerprint) draft = { fingerprint, body: { ...payload, requestId: crypto.randomUUID() } }
  try { receipt.value = await auth.client.request('/api/user/enrollment-applications', { method: 'POST', body: draft.body }); draft = null }
  catch (cause) { error.value = messageOf(cause) } finally { busy.value = false }
}
// 개별 수강신청: 강좌관리에서 허용한 강좌는 학습기간 없이 강좌로 신청한다. 학습기간은 운영기관이 승인할 때 정한다.
async function submitIndividual() {
  if (busy.value || !props.course?.individualApply) return
  busy.value = true; error.value = ''
  const payload = { productId: props.course.id, reason: reason.value.trim() }
  const fingerprint = JSON.stringify(payload)
  if (draft?.fingerprint !== fingerprint) draft = { fingerprint, body: { ...payload, requestId: crypto.randomUUID() } }
  try { receipt.value = await auth.client.request('/api/user/enrollment-applications', { method: 'POST', body: draft.body }); draft = null }
  catch (cause) { error.value = messageOf(cause) } finally { busy.value = false }
}
function close() { if (!busy.value) emit('close') }
</script>
<template>
  <div v-if="course" class="apply-backdrop" @click.self="close">
    <section class="apply-dialog" role="dialog" aria-modal="true" aria-labelledby="apply-title">
      <header><h2 id="apply-title">교육신청하기</h2><button type="button" class="apply-close" aria-label="교육신청 닫기" :disabled="busy" @click="close"><AppIcon name="close" :size="20" /></button></header>
      <div class="apply-body">
        <table class="apply-table"><thead><tr><th scope="col">과정명</th><th scope="col" class="fee">교육비</th></tr></thead>
          <tbody><tr><td>{{ course.name }}<span v-if="course.refund" class="apply-tag">사업주 환급</span></td><td class="fee">{{ fee ? won(fee) : '문의' }}</td></tr></tbody></table>

        <section v-if="receipt" class="apply-done" role="status">
          <h3>수강 신청을 접수했습니다.</h3>
          <p v-if="receipt.state === 'PENDING' && course.individualApply && !choice">운영기관이 학습기간을 정해 승인하면 <strong>진행중인 과정</strong>에서 학습할 수 있습니다.</p><p v-else>소속 기업 교육담당자 또는 운영기관이 승인하면 <strong>진행중인 과정</strong>에서 학습할 수 있습니다.</p>
          <div class="apply-actions"><NuxtLink class="portal-btn" to="/my/enrollment" @click="emit('close')">수강신청 내역 보기</NuxtLink></div>
        </section>
        <section v-else class="apply-self" aria-labelledby="apply-self-title">
          <h3 id="apply-self-title">개인 수강 신청</h3>
          <template v-if="state === 'guest'">
            <p>로그인하면 소속 기업이 신청한 학습기간에 바로 수강 신청할 수 있습니다.</p>
            <div class="apply-actions"><NuxtLink class="portal-btn" :to="loginTo">로그인 후 신청</NuxtLink></div>
          </template>
          <p v-else-if="state === 'loading'" role="status">신청할 수 있는 학습기간을 확인하고 있습니다…</p>
          <p v-else-if="state === 'error'" role="alert" class="apply-error">{{ error }}</p>
          <form v-else-if="!choices.length && course.individualApply" @submit.prevent="submitIndividual">
            <p>이 과정은 <strong>개별 수강신청</strong>이 가능합니다. 신청하면 운영기관이 확인한 뒤 학습기간을 정해 승인합니다.</p>
            <label class="apply-field">신청 메모 (선택)<textarea v-model="reason" maxlength="500" rows="2" placeholder="희망 학습 시작일 등 전달할 내용을 적어 주세요." :disabled="busy" /></label>
            <p v-if="error" role="alert" class="apply-error">{{ error }}</p>
            <div class="apply-actions"><button type="submit" class="portal-btn" :disabled="busy">{{ busy ? '신청 중…' : '개별 수강신청' }}</button></div>
          </form>
          <template v-else-if="!choices.length">
            <p>현재 소속 기업이 이 과정에 신청한 학습기간이 없습니다. 아래 방법으로 교육을 신청해 주세요.</p>
          </template>
          <form v-else @submit.prevent="submit">
            <label class="apply-field">학습기간<select v-model="selected" required :disabled="busy"><option value="" disabled>학습기간을 선택해 주세요</option><option v-for="item in choices" :key="item.openingId" :value="`${item.openingId}`">{{ item.studyStartDate }} ~ {{ item.studyEndDate }}{{ item.companyNm ? ` · ${item.companyNm}` : '' }}</option></select></label>
            <label class="apply-field">신청 메모 (선택)<textarea v-model="reason" maxlength="500" rows="2" placeholder="입력하지 않아도 됩니다." :disabled="busy" /></label>
            <p v-if="error" role="alert" class="apply-error">{{ error }}</p>
            <div class="apply-actions"><button type="submit" class="portal-btn" :disabled="busy || !choice">{{ busy ? '신청 중…' : '수강 신청' }}</button></div>
          </form>
        </section>

        <section class="apply-guide" aria-labelledby="apply-guide-title">
          <h3 id="apply-guide-title">단체(기업) 교육 신청 안내</h3>
          <ol>
            <li>기업 교육담당자가 <strong>기업교육 신청양식</strong>을 작성해 고객센터로 보내 주시면 학습기간과 수강생을 등록해 드립니다.</li>
            <li>등록이 끝나면 수강생은 <strong>나의강의실 &gt; 진행중인 과정</strong>에서 바로 학습할 수 있습니다.</li>
          </ol>
          <div class="apply-actions"><a class="portal-btn secondary" href="/files/company_edu.zip" download><AppIcon name="download" :size="16" />기업교육 신청양식</a><NuxtLink class="portal-btn secondary" to="/support/inquiry" @click="emit('close')">1:1 학습상담</NuxtLink></div>
          <p class="apply-call">고객센터 <a href="tel:02-3142-4151"><strong>02-3142-4151</strong></a> · 평일 09:00~18:00 (점심 12:00~13:00, 주말·공휴일 휴무)</p>
        </section>
      </div>
    </section>
  </div>
</template>
<style scoped>
.apply-backdrop{position:fixed;inset:0;z-index:2000;display:flex;align-items:center;justify-content:center;padding:16px;background:rgba(15,23,42,.55)}
.apply-dialog{width:min(640px,100%);max-height:calc(100vh - 32px);overflow:auto;background:#fff;border-radius:6px;box-shadow:0 20px 50px rgba(0,0,0,.25)}
.apply-dialog header{display:flex;align-items:center;justify-content:space-between;padding:14px 20px;background:#2665ae;color:#fff}
.apply-dialog h2{margin:0;font-size:18px}
.apply-close{display:inline-flex;padding:4px;border:0;background:none;color:#fff;cursor:pointer}
.apply-body{padding:20px}
.apply-table{width:100%;border-collapse:collapse;font-size:14px}
.apply-table th,.apply-table td{padding:10px;border:1px solid #cfd6de;text-align:left}
.apply-table th{background:#eaf2f8;color:#343434}
.apply-table .fee{width:120px;text-align:center;white-space:nowrap}
.apply-tag{display:inline-block;margin-left:8px;padding:0 6px;border-radius:3px;background:#0780c2;color:#fff;font-size:12px;line-height:18px}
.apply-body h3{margin:22px 0 8px;font-size:16px;color:#1f2a37}
.apply-body p{margin:6px 0;font-size:14px;line-height:1.6;color:#455467}
.apply-field{display:block;margin:10px 0;font-size:14px;font-weight:600;color:#343434}
.apply-field select,.apply-field textarea{display:block;width:100%;margin-top:6px;padding:8px;border:1px solid #bfc3c4;border-radius:4px;font:inherit;font-weight:400}
.apply-actions{display:flex;flex-wrap:wrap;gap:8px;margin-top:12px}
.apply-error{color:#c73333 !important}
.apply-done{margin-top:18px;padding:14px;border:1px solid #9cd3b0;border-radius:4px;background:#f1faf4}
.apply-done h3{margin-top:0}
.apply-guide{margin-top:22px;padding-top:4px;border-top:1px solid #e2e8f0}
.apply-guide ol{margin:6px 0;padding-left:20px;font-size:14px;line-height:1.7;color:#455467}
.apply-call{margin-top:12px !important}
.apply-call a{color:#2665ae;text-decoration:none}
</style>
